import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import MuiSwitch, { SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { ChangeEvent, forwardRef, useCallback } from 'react';

export type ISwitchProps = Omit<MuiSwitchProps, 'name' | 'checked' | 'defaultValue' | 'defaultChecked' | 'onChange'> & {
    name: string;
    label?: string;
    enableDOMAttributeRequired?: boolean;
    labelPlacement?: FormControlLabelProps['labelPlacement'];
    value?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>, newValue: boolean) => void;
};

export const Switch = forwardRef<HTMLButtonElement, ISwitchProps>(function InnerSwitch(
    {
        color = 'success',
        enableDOMAttributeRequired = false,
        hidden,
        label,
        labelPlacement = 'end',
        readOnly,
        required = false,
        title,
        value,
        onChange,
        ...rest
    },
    ref
) {
    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
            if (onChange && !readOnly) {
                onChange(event, checked);
            }
        },
        [readOnly, onChange]
    );

    const muiSwitch = (
        <MuiSwitch
            data-testid={`switch_${rest.name}`}
            {...rest}
            ref={ref}
            checked={readOnly ? value === true : value}
            color={color}
            hidden={hidden}
            readOnly={readOnly}
            required={enableDOMAttributeRequired && required}
            sx={hidden ? { ...rest.sx, display: 'none' } : rest.sx}
            onChange={handleChange}
        />
    );

    const formControlLabel = label ? (
        <FormControlLabel value={value} control={muiSwitch} label={label} labelPlacement={labelPlacement} />
    ) : (
        muiSwitch
    );

    return title ? (
        <Tooltip data-testid={`switch_${rest.name}`} title={title} sx={hidden ? { display: 'none' } : undefined}>
            {formControlLabel}
        </Tooltip>
    ) : (
        formControlLabel
    );
});
